// src/pages/FamilyOfficeManager.jsx
// Version étendue : conserve les fonctions existantes et ajoute
// - Clients sans famille (bannière + section cartes)
// - Modals créer/rattacher
// - Rattachement + update rôle user -> user_familyoffice
// - Email depuis auth.users via Edge Function (fallback)
// ----------------------------------------------------------------

import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import {
  Users,
  PlusCircle,
  CheckCircle,
  AlertCircle,
  Filter,
  Trash2,
  Briefcase,
  UserPlus,
  Link as LinkIcon,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import "../styles/FamilyOfficeManager.css";

// ---------- Helpers (hors composant pour éviter des deps instables) ----------
const displayLabelForClient = (c) =>
  c?.display_name?.trim() ||
  [c?.first_name, c?.last_name].filter(Boolean).join(" ").trim() ||
  "Famille";

const ensureUniqueFamilyLabel = (base, families) => {
  const taken = new Set((families || []).map((f) => (f.label || "").toLowerCase().trim()));
  let candidate = (base || "").trim();
  if (!candidate) candidate = "Famille";
  let i = 1;
  while (taken.has(candidate.toLowerCase())) {
    i += 1;
    candidate = `${base} ${i}`;
  }
  return candidate;
};

export default function FamilyOfficeManager() {
  const navigate = useNavigate();

  // ====== ÉTATS EXISTANTS (conservés) ======
  const [families, setFamilies] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [label, setLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentFOId, setCurrentFOId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // ====== NOUVEAUX ÉTATS ======
  const [clientsSansFamille, setClientsSansFamille] = useState([]);
  const [clientFamilyCounts, setClientFamilyCounts] = useState({}); // {family_id: count}
  const [showCreateFamilyModal, setShowCreateFamilyModal] = useState(false);
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);
  const [selectedPendingClient, setSelectedPendingClient] = useState(null);

  const [newFamilyName, setNewFamilyName] = useState("");
  const [newFamilyRole, setNewFamilyRole] = useState("owner");
  const [selectedFamilyId, setSelectedFamilyId] = useState("");
  const [attachRole, setAttachRole] = useState("member");

  // ====== UTILS ======
  const debug = (lbl, data) => console.log(`🔍 [FO-Manager] ${lbl}:`, data);
  const showSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 5000);
  };
  const showError = (message) => {
    setError(message);
    setTimeout(() => setError(null), 7000);
  };

  // ====== AUTH FO ID ======
  const getCurrentFOId = useCallback(async () => {
    const { data: session, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) throw new Error(`Erreur session: ${sessionError.message}`);
    const foId = session?.session?.user?.id;
    if (!foId) throw new Error("Non authentifié");
    setCurrentFOId(foId);
    return foId;
  }, []);

  // ====== FAMILIES (conserve votre logique existante) ======
  const fetchFamilies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const foId = currentFOId || (await getCurrentFOId());

      let q = supabase
        .from("families")
        .select("*")
        .eq("family_office_id", foId)
        .order("created_at", { ascending: false });

      if (statusFilter !== "all") q = q.eq("status", statusFilter);

      const { data, error: fetchError } = await q;
      if (fetchError) throw new Error(`Erreur SQL: ${fetchError.message}`);

      // Comptage des clients via family_clients pour chaque famille
      const counts = {};
      const familiesWithClients = await Promise.all(
        (data || []).map(async (f) => {
          const { data: fc, error: fcErr } = await supabase
            .from("family_clients")
            .select("id, client_id")
            .eq("family_id", f.id);

          if (fcErr) debug(`Erreur family_clients ${f.id}`, fcErr);

          const client_count = fc?.length || 0;
          counts[f.id] = client_count;
          return { ...f, client_count };
        })
      );

      setClientFamilyCounts(counts);
      setFamilies(familiesWithClients);
      setFiltered(familiesWithClients);
    } catch (err) {
      console.error("❌ Erreur fetchFamilies:", err);
      showError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentFOId, statusFilter, getCurrentFOId]);

  // ====== LISTE CLIENTS SANS FAMILLE ======
  // Détection : clients qui ont donné accès au FO via collection_permissions
  // et qui ne figurent pas encore dans family_clients
  const fetchClientsSansFamille = useCallback(async () => {
    try {
      const foId = currentFOId || (await getCurrentFOId());

      // 1) Permissions d'accès données au FO
      const { data: perms, error: permErr } = await supabase
        .from("collection_permissions")
        .select("client_id")
        .eq("grantee_id", foId)
        .eq("can_view", true);

      if (permErr) throw permErr;

      const uniqueClientIds = [...new Set((perms || []).map((p) => p.client_id).filter(Boolean))];
      if (uniqueClientIds.length === 0) {
        setClientsSansFamille([]);
        return;
      }

      // 2) Clients déjà rattachés ?
      const { data: familyLinks, error: linkErr } = await supabase
        .from("family_clients")
        .select("client_id")
        .in("client_id", uniqueClientIds);

      if (linkErr) throw linkErr;

      const attached = new Set((familyLinks || []).map((l) => l.client_id));
      const pendingIds = uniqueClientIds.filter((id) => !attached.has(id));
      if (pendingIds.length === 0) {
        setClientsSansFamille([]);
        return;
      }

      // 3) Profils basiques (nom/société)
      const { data: profiles, error: profileErr } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, company, created_at, role")
        .in("id", pendingIds);

      if (profileErr) throw profileErr;

      // 4) Emails depuis auth.users via Edge Function (sécurisée côté serveur)
      //    Fonction: fo-get-user-emails { ids: string[] } -> [{id, email}]
      let emailMap = {};
      try {
        const { data: fnData, error: fnErr } = await supabase.functions.invoke("fo-get-user-emails", {
          body: { ids: pendingIds },
        });
        if (fnErr) throw fnErr;
        (fnData || []).forEach((u) => {
          emailMap[u.id] = u.email;
        });
      } catch (e) {
        debug("Edge Function email indisponible (fallback)", e?.message || e);
      }

      const pendingClients = (profiles || []).map((p) => ({
        id: p.id,
        display_name:
          p.company || [p.first_name, p.last_name].filter(Boolean).join(" ") || "Client anonyme",
        email: emailMap[p.id] || "Email non disponible",
        created_at: p.created_at,
        role: p.role || "user",
      }));

      setClientsSansFamille(pendingClients);
    } catch (err) {
      console.error("❌ Erreur fetchClientsSansFamille:", err);
      showError("Erreur lors du chargement des clients en attente");
    }
  }, [currentFOId, getCurrentFOId]);

  // ====== CRÉATION “MANUELLE” (bloc existant : input label + notes) ======
  const createFamily = useCallback(async () => {
    if (!label.trim()) return;
    setLoading(true);
    setError(null);

    try {
      const foId = currentFOId || (await getCurrentFOId());

      const { data: newFam, error: famErr } = await supabase
        .from("families")
        .insert({
          label: label.trim(),
          notes: notes?.trim() || null,
          status: "active",
          family_office_id: foId,
        })
        .select()
        .single();

      if (famErr) throw famErr;

      showSuccess(`Famille “${newFam.label}” créée`);
      setLabel("");
      setNotes("");
      await fetchFamilies();
    } catch (err) {
      console.error("❌ Erreur createFamily:", err);
      showError(err.message || "Erreur lors de la création de la famille");
    } finally {
      setLoading(false);
    }
  }, [label, notes, currentFOId, getCurrentFOId, fetchFamilies]);

  // ====== CRÉER FAMILLE POUR UN CLIENT (Quick) ======
  // 🎯 Création instantanée : crée une famille et met ce client en chef (owner + is_primary)
  const handleQuickCreateFamily = useCallback(
    async (client) => {
      if (!client?.id) return;
      setProcessingAction(true);
      try {
        const foId = currentFOId || (await getCurrentFOId());

        // Nom automatique unique: "Famille {Nom Affiché}"
        const base = `Famille ${displayLabelForClient(client)}`;
        const labelAuto = ensureUniqueFamilyLabel(base, families);

        // 1) Créer la famille (renseigne le client_profile_id immédiatement)
        const { data: fam, error: famErr } = await supabase
          .from("families")
          .insert({
            label: labelAuto,
            family_office_id: foId,
            client_profile_id: client.id, // chef connu au niveau famille
            status: "active",
          })
          .select()
          .single();

        if (famErr) throw famErr;

        // 2) Lier le client comme OWNER + is_primary
        const { error: linkErr } = await supabase.from("family_clients").insert({
          family_id: fam.id,
          client_id: client.id,
          role: "owner",
          is_primary: true,
        });
        if (linkErr) throw linkErr;

        // 3) Mettre à jour le rôle du profil
        const { error: roleErr } = await supabase
          .from("profiles")
          .update({ role: "user_familyoffice" })
          .eq("id", client.id);
        if (roleErr) console.warn("Update rôle (non bloquant):", roleErr);

        showSuccess(`Famille “${fam.label}” créée — ${client.display_name} défini chef de famille`);
        await Promise.all([fetchFamilies(), fetchClientsSansFamille()]);
      } catch (e) {
        console.error("Quick create error:", e);
        showError("Erreur lors de la création de la famille");
      } finally {
        setProcessingAction(false);
      }
    },
    [currentFOId, getCurrentFOId, families, fetchFamilies, fetchClientsSansFamille]
  );

  // ====== CRÉER FAMILLE POUR UN CLIENT (Modal) ======
  const handleCreateFamily = useCallback(async () => {
    if (!newFamilyName.trim() || !selectedPendingClient) {
      showError("Veuillez renseigner le nom de la famille.");
      return;
    }
    setProcessingAction(true);
    try {
      const foId = currentFOId || (await getCurrentFOId());

      // 1) Créer la famille
      const { data: newFam, error: famErr } = await supabase
        .from("families")
        .insert({
          label: newFamilyName.trim(),
          family_office_id: foId,
          status: "active",
          notes: null,
        })
        .select()
        .single();

      if (famErr) throw famErr;

      // 2) Lier le client
      const { error: linkErr } = await supabase.from("family_clients").insert({
        family_id: newFam.id,
        client_id: selectedPendingClient.id,
        role: newFamilyRole,
        is_primary: true,
      });

      if (linkErr) throw linkErr;

      // 3) Mettre à jour le rôle
      const { error: roleErr } = await supabase
        .from("profiles")
        .update({ role: "user_familyoffice" })
        .eq("id", selectedPendingClient.id);
      if (roleErr) debug("⚠️ Update rôle échoué (non bloquant)", roleErr);

      showSuccess(`Famille “${newFam.label}” créée et client rattaché`);

      // Reset + refresh
      setShowCreateFamilyModal(false);
      setNewFamilyName("");
      setNewFamilyRole("owner");
      setSelectedPendingClient(null);

      await Promise.all([fetchFamilies(), fetchClientsSansFamille()]);
    } catch (err) {
      console.error("❌ Erreur création famille:", err);
      showError("Erreur lors de la création de la famille");
    } finally {
      setProcessingAction(false);
    }
  }, [
    newFamilyName,
    selectedPendingClient,
    newFamilyRole,
    currentFOId,
    getCurrentFOId,
    fetchFamilies,
    fetchClientsSansFamille,
  ]);

  // ====== RATTACHER À FAMILLE EXISTANTE (Modal) ======
  const handleAttachToFamily = useCallback(async () => {
    if (!selectedFamilyId || !selectedPendingClient) {
      showError("Veuillez sélectionner une famille et un client.");
      return;
    }
    setProcessingAction(true);
    try {
      // 1) Lier le client
      const { error: linkErr } = await supabase.from("family_clients").insert({
        family_id: selectedFamilyId,
        client_id: selectedPendingClient.id,
        role: attachRole,
        is_primary: false,
      });
      if (linkErr) throw linkErr;

      // 2) Mettre à jour le rôle
      const { error: roleErr } = await supabase
        .from("profiles")
        .update({ role: "user_familyoffice" })
        .eq("id", selectedPendingClient.id);
      if (roleErr) debug("⚠️ Update rôle échoué (non bloquant)", roleErr);

      showSuccess("Client rattaché avec succès");
      setShowAttachModal(false);
      setSelectedFamilyId("");
      setAttachRole("member");
      setSelectedPendingClient(null);

      await Promise.all([fetchFamilies(), fetchClientsSansFamille()]);
    } catch (err) {
      console.error("❌ Erreur rattachement:", err);
      showError("Erreur lors du rattachement");
    } finally {
      setProcessingAction(false);
    }
  }, [
    selectedFamilyId,
    selectedPendingClient,
    attachRole,
    fetchFamilies,
    fetchClientsSansFamille,
  ]);

  // ====== SUPPRESSION / OUVERTURE (conservé) ======
  const deleteFamily = useCallback(
    async (familyId, familyLabel) => {
      if (
        !window.confirm(
          `Supprimer la famille "${familyLabel}" ?\n\n` +
            `⚠️ Cette action supprimera la famille mais pas les clients ni leurs collections.\n` +
            `Les liens dans family_clients seront supprimés automatiquement.`
        )
      ) {
        return;
      }
      setDeletingId(familyId);
      try {
        const { error: deleteError } = await supabase.from("families").delete().eq("id", familyId);
        if (deleteError) throw deleteError;
        showSuccess(`Famille "${familyLabel}" supprimée`);
        await fetchFamilies();
      } catch (err) {
        console.error("❌ Erreur suppression:", err);
        showError(err.message);
      } finally {
        setDeletingId(null);
      }
    },
    [fetchFamilies]
  );

  const openFamily = useCallback((familyId) => navigate(`/family-office/families/${familyId}`), [navigate]);

  // ====== EFFECTS ======
  // ✅ Fix ESLint: depend on the callback itself, not just statusFilter
  useEffect(() => {
    fetchFamilies();
  }, [fetchFamilies]);

  useEffect(() => {
    // Recherche locale (conservée)
    if (!families.length) {
      setFiltered([]);
      return;
    }
    if (!query.trim()) {
      setFiltered(families);
      return;
    }
    const q = query.toLowerCase();
    setFiltered(
      families.filter(
        (f) =>
          (f.label || "").toLowerCase().includes(q) || (f.notes || "").toLowerCase().includes(q)
      )
    );
  }, [query, families]);

  useEffect(() => {
    // Charger la liste “en attente”
    fetchClientsSansFamille();
  }, [fetchClientsSansFamille]);

  // ====== RENDER ======
  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Header */}
      <div className="border-b border-[#E8E8E8] bg-white">
        <div className="max-w-7xl mx-auto px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-light text-[#1A1A1A] mb-2">Gestion des familles</h1>
              <p className="text-sm text-[#666]">Créez et organisez les familles de votre portefeuille</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-12 space-y-8">
        {/* Messages */}
        {success && (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-4 flex items-start gap-3">
            <CheckCircle className="text-emerald-600 shrink-0" size={20} />
            <p className="text-sm text-emerald-800">{success}</p>
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-4 flex items-start gap-3">
            <AlertCircle className="text-red-600 shrink-0" size={20} />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* 🟧 BANNIÈRE ALERTE : Clients en attente */}
        {clientsSansFamille.length > 0 && (
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-l-4 border-orange-500 rounded-lg shadow-sm overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                    <AlertCircle className="text-white" size={24} />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-light text-[#1A1A1A] mb-2">
                    ⚠️ {clientsSansFamille.length} client
                    {clientsSansFamille.length > 1 ? "s" : ""} en attente d'organisation
                  </h3>
                  <p className="text-sm text-[#666] mb-4 leading-relaxed">
                    Ces clients vous ont donné accès à leurs collections mais ne sont pas encore rattachés à une famille.
                  </p>

                  {clientsSansFamille.length > 0 && (
                    <div className="mt-4">
                      <button
                        onClick={async () => {
                          setProcessingAction(true);
                          try {
                            for (const c of clientsSansFamille) {
                              await handleQuickCreateFamily(c);
                            }
                            showSuccess("Toutes les familles ont été créées avec leurs chefs !");
                          } catch (e) {
                            console.error(e);
                            showError("Certaines créations ont échoué. Vérifiez les logs.");
                          } finally {
                            setProcessingAction(false);
                          }
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] text-white text-sm rounded hover:bg-[#333] transition-colors"
                        disabled={processingAction}
                      >
                        Créer les familles pour tous ({clientsSansFamille.length})
                      </button>
                    </div>
                  )}

                  <button
                    onClick={() =>
                      document.getElementById("clients-sans-famille")?.scrollIntoView({ behavior: "smooth" })
                    }
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 transition-colors"
                  >
                    <Users size={16} />
                    Voir les clients ({clientsSansFamille.length})
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 🟧 SECTION : Clients en attente d'organisation */}
        {clientsSansFamille.length > 0 && (
          <div id="clients-sans-famille" className="bg-white rounded border border-[#E8E8E8] shadow-sm overflow-hidden">
            <div className="px-8 py-6 border-b border-[#E8E8E8]">
              <h2 className="text-lg font-light text-[#1A1A1A]">Clients en attente d’organisation</h2>
              <p className="text-sm text-[#666]">Créez une famille ou rattachez ces clients à une famille existante.</p>
            </div>

            <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {clientsSansFamille.map((client) => (
                <div
                  key={client.id}
                  className="bg-white border-2 border-orange-200 rounded-lg p-6 hover:border-orange-400 transition-all shadow-sm"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-light text-lg">
                        {(client.display_name?.[0] || "?").toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-normal text-[#1A1A1A] mb-1 truncate">{client.display_name}</h3>
                      <p className="text-xs text-[#666] truncate">{client.email}</p>
                      <span className="inline-block mt-2 px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded">
                        En attente
                      </span>
                    </div>
                  </div>

                  <div className="mb-4 pb-4 border-b border-[#E8E8E8]">
                    <div className="text-xs text-[#999]">Accès donné le</div>
                    <div className="text-sm text-[#1A1A1A]">
                      {client.created_at
                        ? new Date(client.created_at).toLocaleDateString("fr-FR", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })
                        : "—"}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setSelectedPendingClient(client);
                        setShowCreateFamilyModal(true);
                      }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#1A1A1A] text-white text-sm rounded hover:bg-[#333] transition-colors"
                    >
                      <UserPlus size={16} />
                      Nouvelle famille
                    </button>

                    <button
                      onClick={() => {
                        setSelectedPendingClient(client);
                        setShowAttachModal(true);
                      }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-[#E8E8E8] text-[#1A1A1A] text-sm rounded hover:border-[#1A1A1A] transition-colors"
                    >
                      <LinkIcon size={16} />
                      Rattacher à une famille
                    </button>

                    <button
                      onClick={() => handleQuickCreateFamily(client)}
                      disabled={processingAction}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 transition-colors disabled:opacity-50"
                      title="Créer une famille et définir ce client comme chef"
                    >
                      Créer famille (chef)
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===== Création (existant) ===== */}
        <div className="bg-white rounded border border-[#E8E8E8] shadow-sm overflow-hidden">
          <div className="border-b border-[#E8E8E8] px-8 py-6">
            <h2 className="text-lg font-light text-[#1A1A1A]">Créer une nouvelle famille</h2>
          </div>

          <div className="px-8 py-8 space-y-4">
            <div className="flex gap-4">
              <input
                type="text"
                placeholder="Nom de la famille..."
                className="flex-1 border border-[#E8E8E8] rounded px-4 py-3 text-sm text-[#1A1A1A] placeholder:text-[#999] focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !loading) createFamily();
                }}
                disabled={loading}
              />

              <button
                onClick={createFamily}
                disabled={loading || !label.trim()}
                className={`flex items-center gap-2 px-6 py-3 rounded text-sm font-light transition-all shrink-0 ${
                  loading || !label.trim()
                    ? "bg-[#E8E8E8] text-[#999] cursor-not-allowed"
                    : "bg-[#1A1A1A] text-white hover:bg-[#333]"
                }`}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Création...</span>
                  </>
                ) : (
                  <>
                    <PlusCircle size={16} />
                    <span>Créer</span>
                  </>
                )}
              </button>
            </div>

            <textarea
              placeholder="Notes (optionnel)..."
              className="w-full border border-[#E8E8E8] rounded px-4 py-3 text-sm text-[#1A1A1A] placeholder:text-[#999] focus:outline-none focus:ring-2 focus:ring-[#1A1A1A] resize-none"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
            />

            <p className="text-xs text-[#999] font-light">💡 Astuce : Après avoir créé la famille, vous pourrez y ajouter des clients</p>
          </div>
        </div>

        {/* ===== Recherche/Filtres (existant) ===== */}
        <div className="bg-white rounded border border-[#E8E8E8] shadow-sm overflow-hidden">
          <div className="px-8 py-6 space-y-4">
            <input
              className="w-full border-0 outline-none text-sm text-[#1A1A1A] placeholder:text-[#999]"
              placeholder="Rechercher une famille ou des notes..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />

            <div className="flex items-center gap-3 pt-4 border-t border-[#E8E8E8]">
              <Filter size={16} className="text-[#999]" />
              <div className="flex gap-2">
                {["all", "active", "inactive"].map((status) => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`px-3 py-1 rounded text-xs font-light transition-all ${
                      statusFilter === status ? "bg-[#1A1A1A] text-white" : "bg-[#F5F5F5] text-[#666] hover:bg-[#E8E8E8]"
                    }`}
                  >
                    {status === "all" ? "Toutes" : status === "active" ? "Actives" : "Inactives"}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ===== Liste familles (existant) ===== */}
        <div className="bg-white rounded border border-[#E8E8E8] shadow-sm overflow-hidden">
          <div className="border-b border-[#E8E8E8] px-8 py-6">
            <h2 className="text-lg font-light text-[#1A1A1A]">
              Familles gérées <span className="ml-2 text-[#999]">({filtered.length})</span>
            </h2>
          </div>

          <div className="divide-y divide-[#E8E8E8]">
            {loading ? (
              <div className="px-8 py-12 text-center">
                <div className="inline-block w-8 h-8 border-2 border-[#E8E8E8] border-t-[#1A1A1A] rounded-full animate-spin" />
                <p className="mt-4 text-sm text-[#999]">Chargement...</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-8 py-12 text-center">
                <Users className="mx-auto mb-4 text-[#CCC]" size={48} strokeWidth={1} />
                <p className="text-sm text-[#999] font-light">
                  {query
                    ? "Aucune famille trouvée"
                    : statusFilter !== "all"
                    ? `Aucune famille ${statusFilter === "active" ? "active" : "inactive"}`
                    : "Aucune famille créée"}
                </p>
              </div>
            ) : (
              filtered.map((f) => (
                <div
                  key={f.id}
                  className="px-8 py-6 hover:bg-[#FAFAF8] transition-colors flex items-center justify-between gap-6"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-base font-light text-[#1A1A1A] truncate">{f.label}</h3>
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-light ${
                          f.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                        }`}
                      >
                        {f.status}
                      </span>
                    </div>

                    <div className="flex items-center gap-4 text-xs text-[#999]">
                      <span className="flex items-center gap-1">
                        <Briefcase size={12} />
                        {(clientFamilyCounts[f.id] ?? f.client_count ?? 0)} client
                        {(clientFamilyCounts[f.id] ?? f.client_count ?? 0) > 1 ? "s" : ""}
                      </span>
                      {f.created_at && (
                        <span>
                          Créée le{" "}
                          {new Date(f.created_at).toLocaleDateString("fr-FR", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })}
                        </span>
                      )}
                    </div>

                    {f.notes && <p className="mt-2 text-xs text-[#666] italic truncate">{f.notes}</p>}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      className="px-5 py-2 text-sm font-light rounded border border-[#E8E8E8] hover:bg-[#1A1A1A] hover:text-white hover:border-[#1A1A1A] transition-all"
                      onClick={() => openFamily(f.id)}
                    >
                      Ouvrir
                    </button>

                    <button
                      className={`p-2 text-sm font-light rounded border border-[#E8E8E8] hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-all ${
                        deletingId === f.id ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      onClick={() => deleteFamily(f.id, f.label)}
                      disabled={deletingId === f.id}
                      title="Supprimer la famille"
                    >
                      {deletingId === f.id ? (
                        <div className="w-4 h-4 border-2 border-red-300 border-t-red-600 rounded-full animate-spin" />
                      ) : (
                        <Trash2 size={16} />
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* MODAL : Créer une nouvelle famille pour ce client */}
      {showCreateFamilyModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => !processingAction && setShowCreateFamilyModal(false)}
        >
          <div className="bg-white rounded-lg max-w-md w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-[#E8E8E8]">
              <h3 className="text-xl font-light text-[#1A1A1A]">Créer une nouvelle famille</h3>
              <button
                onClick={() => !processingAction && setShowCreateFamilyModal(false)}
                className="text-[#999] hover:text-[#1A1A1A] transition-colors"
                disabled={processingAction}
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {selectedPendingClient && (
                <div className="p-4 bg-gray-50 rounded border border-[#E8E8E8]">
                  <div className="text-xs text-[#999] mb-1">Pour le client</div>
                  <div className="text-sm font-normal text-[#1A1A1A]">{selectedPendingClient.display_name}</div>
                  <div className="text-xs text-[#666]">{selectedPendingClient.email}</div>
                </div>
              )}

              <div>
                <label className="block text-sm text-[#666] mb-2">Nom de la famille *</label>
                <input
                  type="text"
                  value={newFamilyName}
                  onChange={(e) => setNewFamilyName(e.target.value)}
                  placeholder="Ex: Famille Dupont"
                  className="w-full px-4 py-3 border border-[#E8E8E8] rounded text-sm focus:outline-none focus:border-[#1A1A1A]"
                  disabled={processingAction}
                />
              </div>

              <div>
                <label className="block text-sm text-[#666] mb-2">Rôle du client dans la famille *</label>
                <select
                  value={newFamilyRole}
                  onChange={(e) => setNewFamilyRole(e.target.value)}
                  className="w-full px-4 py-3 border border-[#E8E8E8] rounded text-sm focus:outline-none focus:border-[#1A1A1A]"
                  disabled={processingAction}
                >
                  <option value="owner">Propriétaire principal</option>
                  <option value="spouse">Conjoint</option>
                  <option value="child">Enfant</option>
                  <option value="parent">Parent</option>
                  <option value="member">Autre membre</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-[#E8E8E8]">
              <button
                onClick={() => setShowCreateFamilyModal(false)}
                className="px-4 py-2 text-sm text-[#666] hover:text-[#1A1A1A] transition-colors"
                disabled={processingAction}
              >
                Annuler
              </button>
              <button
                onClick={handleCreateFamily}
                disabled={!newFamilyName.trim() || processingAction}
                className="px-6 py-2 bg-[#1A1A1A] text-white text-sm rounded hover:bg-[#333] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processingAction ? "Création..." : "Créer la famille"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL : Rattacher à une famille existante */}
      {showAttachModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => !processingAction && setShowAttachModal(false)}
        >
          <div className="bg-white rounded-lg max-w-md w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-[#E8E8E8]">
              <h3 className="text-xl font-light text-[#1A1A1A]">Rattacher à une famille</h3>
              <button
                onClick={() => !processingAction && setShowAttachModal(false)}
                className="text-[#999] hover:text-[#1A1A1A] transition-colors"
                disabled={processingAction}
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {selectedPendingClient && (
                <div className="p-4 bg-gray-50 rounded border border-[#E8E8E8]">
                  <div className="text-xs text-[#999] mb-1">Client à rattacher</div>
                  <div className="text-sm font-normal text-[#1A1A1A]">{selectedPendingClient.display_name}</div>
                  <div className="text-xs text-[#666]">{selectedPendingClient.email}</div>
                </div>
              )}

              <div>
                <label className="block text-sm text-[#666] mb-2">Sélectionner une famille *</label>
                <select
                  value={selectedFamilyId}
                  onChange={(e) => setSelectedFamilyId(e.target.value)}
                  className="w-full px-4 py-3 border border-[#E8E8E8] rounded text-sm focus:outline-none focus:border-[#1A1A1A]"
                  disabled={processingAction}
                >
                  <option value="">Choisir une famille...</option>
                  {families.map((fam) => (
                    <option key={fam.id} value={fam.id}>
                      {fam.label} ({(clientFamilyCounts[fam.id] ?? fam.client_count ?? 0)} membre
                      {(clientFamilyCounts[fam.id] ?? fam.client_count ?? 0) > 1 ? "s" : ""})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-[#666] mb-2">Rôle dans la famille *</label>
                <select
                  value={attachRole}
                  onChange={(e) => setAttachRole(e.target.value)}
                  className="w-full px-4 py-3 border border-[#E8E8E8] rounded text-sm focus:outline-none focus:border-[#1A1A1A]"
                  disabled={processingAction}
                >
                  <option value="member">Membre</option>
                  <option value="owner">Propriétaire</option>
                  <option value="spouse">Conjoint</option>
                  <option value="child">Enfant</option>
                  <option value="parent">Parent</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-[#E8E8E8]">
              <button
                onClick={() => setShowAttachModal(false)}
                className="px-4 py-2 text-sm text-[#666] hover:text-[#1A1A1A] transition-colors"
                disabled={processingAction}
              >
                Annuler
              </button>
              <button
                onClick={handleAttachToFamily}
                disabled={!selectedFamilyId || processingAction}
                className="px-6 py-2 bg-[#1A1A1A] text-white text-sm rounded hover:bg-[#333] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processingAction ? "Rattachement..." : "Rattacher"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
